import { formatISO, parseISO } from "date-fns";

import type {
  DistributiveAtLeastOneOf,
  ISO8601DateTimeString,
  ProjectId,
  SelectivePartial,
  UserId,
  UUIDv4,
} from "../types";

type CustomFieldId = CustomField["id"];

export const CustomFieldDataTypes = [
  "boolean",
  "date",
  "multi_select",
  "numeric",
  "select",
  "text",
] as const;

export const CommonStandardFieldDataTypes = [
  "barcode",
  "category",
  "description",
  "id",
  "location",
  "status",
  "date",
  "userId",
  "statusCompletions",
] as const;

export type CommonStandardFieldDataType =
  (typeof CommonStandardFieldDataTypes)[number];
export type CustomFieldDataType = (typeof CustomFieldDataTypes)[number];
export type DocumentFieldDataType = "sheets" | "models";
export type CustomFieldDataValue = Exclude<
  CustomField["defaultValue"],
  undefined
>;
export type CustomFieldDTODataValue = Exclude<
  CustomFieldDTO["defaultValue"],
  undefined
>;
export type FieldDataType =
  | CommonStandardFieldDataType
  | CustomFieldDataType
  | DocumentFieldDataType;

export type AssetFieldKey = string;
export type AssetFieldValue = CustomField["defaultValue"]; // All standard fields are also covered

type MinimumField<
  T extends FieldDataType,
  K extends AssetFieldKey = AssetFieldKey,
> = {
  dataType: T;
  name: K;
};

type CommonStandardField<
  T extends FieldDataType,
  K extends AssetFieldKey = AssetFieldKey,
> = MinimumField<T, K> & {
  displayName: string;
};

type CommonCustomField<T extends FieldDataType> = MinimumField<T> & {
  createdAt: Date;
  createdBy: UserId;
  deletedAt?: Date;
  deletedBy?: UserId;
  description?: string | null;
  displayName: string;
  id: UUIDv4;
  isActive: boolean;
  projectId: ProjectId;
  requiredOnIngress: boolean;
  updatedAt: Date;
  updatedBy: UserId;
  version: number;
};

type CommonCustomFieldDTO<T extends CustomField> = Omit<
  T,
  "createdAt" | "deletedAt" | "updatedAt"
> & {
  createdAt: ISO8601DateTimeString;
  deletedAt?: ISO8601DateTimeString;
  updatedAt: ISO8601DateTimeString;
  defaultValue: T["defaultValue"];
};

export type CustomSelectFieldValue = {
  createdAt?: Date;
  createdBy?: UserId;
  deletedAt?: Date;
  deletedBy?: UserId;
  customAttributeId: CustomFieldId;
  displayName: string;
  id: UUIDv4;
  isActive: boolean;
  projectId?: ProjectId;
  updatedAt?: Date;
  updatedBy?: UserId;
  version?: number;
};

export type CustomSelectFieldValueDTO = Omit<
  CustomSelectFieldValue,
  "createdAt" | "deletedAt" | "updatedAt"
> & {
  createdAt?: ISO8601DateTimeString;
  deletedAt?: ISO8601DateTimeString;
  updatedAt?: ISO8601DateTimeString;
};

export type BarcodeField = CommonStandardField<"barcode", "barcode">;
export type CategoryField = CommonStandardField<"category", "categoryId">;
export type DescriptionField = CommonStandardField<
  "description",
  "description"
>;
export type IdField = CommonStandardField<"id", "clientAssetId">;
export type LocationField = CommonStandardField<"location", "locationId">;
export type StatusField = CommonStandardField<"status", "statusId">;
export type CreatedAtField = CommonStandardField<"date", "createdAt">;
export type CreatedByField = CommonStandardField<"userId", "createdBy">;
export type UpdatedAtField = CommonStandardField<"date", "updatedAt">;
export type UpdatedByField = CommonStandardField<"userId", "updatedBy">;
export type DeletedAtField = CommonStandardField<"date", "deletedAt">;
export type DeletedByField = CommonStandardField<"userId", "deletedBy">;

export type CustomTextField = CommonCustomField<"text"> & {
  dataType: "text";
  defaultValue?: string | null;
  maxLengthOnIngress?: number | null;
};
export type CustomBooleanField = CommonCustomField<"boolean"> & {
  dataType: "boolean";
  defaultValue?: boolean | null;
};
export type CustomNumericField = CommonCustomField<"numeric"> & {
  dataType: "numeric";
  defaultValue?: number | null;
};
export type CustomDateField = CommonCustomField<"date"> & {
  defaultValue?: Date | null;
};
export type CustomSelectField = CommonCustomField<"select"> & {
  dataType: "select";
  defaultValue?: string | null;
  enumValues: string[];
  values: CustomSelectFieldValue[];
};
export type CustomMultiSelectField = CommonCustomField<"multi_select"> & {
  dataType: "multi_select";
  defaultValue?: string[] | null;
  enumValues: string[];
  values: CustomSelectFieldValue[];
};

export type StandardField =
  | BarcodeField
  | CategoryField
  | DescriptionField
  | IdField
  | LocationField
  | StatusField;

export type StandardSortableField =
  | StandardField
  | CreatedAtField
  | CreatedByField
  | UpdatedAtField
  | UpdatedByField
  | DeletedAtField
  | DeletedByField;

export type CustomField =
  | CustomBooleanField
  | CustomDateField
  | CustomMultiSelectField
  | CustomNumericField
  | CustomSelectField
  | CustomTextField;

export type NewCustomField<T extends CustomField = CustomField> =
  T extends CustomField
    ? Pick<
        T,
        | "dataType"
        | Exclude<Extract<keyof T, CustomFieldEditableFieldKey>, "isActive">
      >
    : never;

export type CustomFieldUpdate = DistributiveAtLeastOneOf<
  CustomField,
  Exclude<CustomFieldEditableFieldKey, "isActive">
>;

type CustomBooleanFieldDTO = CommonCustomFieldDTO<CustomBooleanField>;
type CustomDateFieldDTO = Omit<
  CommonCustomFieldDTO<CustomDateField>,
  "defaultValue"
> & {
  defaultValue: ISO8601DateTimeString | null;
};
export type CustomMultiSelectFieldDTO = Omit<
  CommonCustomFieldDTO<CustomMultiSelectField>,
  "values"
> & {
  values: CustomSelectFieldValueDTO[];
};
type CustomNumericFieldDTO = Omit<
  CommonCustomFieldDTO<CustomNumericField>,
  "defaultValue"
> & {
  defaultValue: string | null;
};
export type CustomSelectFieldDTO = Omit<
  CommonCustomFieldDTO<CustomSelectField>,
  "values"
> & {
  values: CustomSelectFieldValueDTO[];
};
type CustomTextFieldDTO = CommonCustomFieldDTO<CustomTextField>;

export type CustomFieldDTO =
  | CustomBooleanFieldDTO
  | CustomDateFieldDTO
  | CustomMultiSelectFieldDTO
  | CustomNumericFieldDTO
  | CustomSelectFieldDTO
  | CustomTextFieldDTO;

export type Field = StandardSortableField | CustomField;

export type CustomFieldEditableFieldKey<T extends CustomField = CustomField> =
  Extract<
    T extends CustomField ? keyof T : never,
    "defaultValue" | "description" | "displayName" | "enumValues"
  >;

export type CustomBooleanFilter = {
  dataType: CustomBooleanField["dataType"];
  value: CustomBooleanField["defaultValue"];
};

export type CustomDateFilter = {
  dataType: CustomDateField["dataType"];
  value:
    | CustomDateField["defaultValue"]
    // date range
    | [CustomDateField["defaultValue"], CustomDateField["defaultValue"]];
};

export type CustomMultiSelectFilter = {
  dataType: CustomMultiSelectField["dataType"];
  value: CustomMultiSelectField["defaultValue"];
};

export type CustomNumericFilter = {
  dataType: CustomNumericField["dataType"];
  value:
    | CustomNumericField["defaultValue"]
    // numeric range
    | [CustomNumericField["defaultValue"], CustomNumericField["defaultValue"]];
};

export type NumericFilterCondition =
  | "equalTo"
  | "lessThanOrEqualTo"
  | "greaterThanOrEqualTo"
  | "between";

export type CustomSelectFilter = {
  dataType: CustomSelectField["dataType"];
  value:
    | Exclude<CustomSelectField["defaultValue"], null | undefined>[]
    | null
    | undefined;
};

export type CustomTextFilter = {
  dataType: CustomTextField["dataType"];
  value: CustomTextField["defaultValue"];
};

export type CustomFieldFilters = Record<
  CustomField["name"],
  | CustomBooleanFilter
  | CustomDateFilter
  | CustomMultiSelectFilter
  | CustomNumericFilter
  | CustomSelectFilter
  | CustomTextFilter
>;

export type CustomFieldFilterValue =
  | boolean
  | number
  | string
  | string[]
  | null
  | undefined;

// Lib-only pure string array (no i18n dependency); mirrors all keys of the
// i18n-bound standardFieldMap that lives in each consumer's residue.
export const standardFieldMapKeys = [
  "clientAssetId",
  "description",
  "statusId",
  "categoryId",
  "locationId",
  "barcode",
  "createdAt",
  "createdBy",
  "updatedAt",
  "updatedBy",
  "deletedAt",
  "deletedBy",
];

const isArrayOfTwoNumbers = (
  value: Exclude<CustomNumericFilter["value"], null | undefined | number>
): value is [number, number] => value[0] != null && value[1] != null; // intentional coercion `undefined == null`

const getNumericRangeValue = (
  value: Exclude<CustomNumericFilter["value"], null | undefined | number>
): string => {
  const sortedValue = isArrayOfTwoNumbers(value)
    ? value.slice().sort((a, b) => a - b)
    : value;

  return `${sortedValue[0] != null ? sortedValue[0] : ""}..${
    sortedValue[1] != null ? sortedValue[1] : ""
  }`;
};

export const formatDateForBackend = (date: Date) =>
  formatISO(date, { representation: "date" });

// Forge filter spec: https://git.autodesk.com/forge/forge-api-standards/blob/master/Filtering.md
export const buildCustomFieldFilters = (
  customFields: CustomFieldFilters,
  useNamesAsKeys?: boolean
): Record<string, CustomFieldFilterValue> =>
  Object.entries(customFields).reduce(
    (filters: Record<string, CustomFieldFilterValue>, [key, specifier]) => {
      let value: CustomFieldFilterValue;

      switch (specifier.dataType) {
        case "boolean":
          value = specifier.value;
          break;
        case "multi_select":
        case "select":
        case "text":
          value =
            specifier.value === undefined
              ? undefined
              : Array.isArray(specifier.value)
                ? specifier.value.filter(Boolean)
                : specifier.value || null;

          if (Array.isArray(value) && !value.length) {
            value = null;
          }
          break;
        case "date":
          value = !Array.isArray(specifier.value)
            ? specifier.value && formatDateForBackend(specifier.value)
            : specifier.value[0] || specifier.value[1]
              ? `${
                  specifier.value[0]
                    ? formatDateForBackend(specifier.value[0])
                    : ""
                }..${
                  specifier.value[1]
                    ? formatDateForBackend(specifier.value[1])
                    : ""
                }`
              : undefined; // bogus value; drop it
          break;
        case "numeric":
          value = !Array.isArray(specifier.value)
            ? specifier.value
            : specifier.value[0] != null || specifier.value[1] != null // intentional coercion `undefined == null`
              ? getNumericRangeValue(specifier.value)
              : undefined; // bogus value; drop it
          break;
      }

      if (value !== undefined) {
        filters[useNamesAsKeys ? key : `filter[customAttributes][${key}]`] =
          value;
      }

      return filters;
    },
    {}
  );

export const parseCustomField = (
  field: CustomFieldDTO | undefined
): CustomField | undefined => {
  if (!field) {
    return undefined;
  }

  const parsedDates = {
    createdAt: new Date(field.createdAt),
    deletedAt: field.deletedAt && new Date(field.deletedAt),
    updatedAt: new Date(field.updatedAt),
  };

  switch (field.dataType) {
    case "numeric": {
      return {
        ...field,
        ...parsedDates,
        defaultValue:
          (field.defaultValue && Number(field.defaultValue)) || null,
      } as CustomNumericField;
    }
    case "date": {
      return {
        ...field,
        ...parsedDates,
        defaultValue:
          (field.defaultValue && parseISO(field.defaultValue)) || null,
      } as CustomDateField;
    }
    case "text":
    case "boolean":
      return {
        ...field,
        ...parsedDates,
      } as CustomBooleanField | CustomTextField;
    case "select":
    case "multi_select":
      return {
        ...field,
        ...parsedDates,
        values:
          field.values &&
          field.values.map(({ createdAt, deletedAt, updatedAt, ...value }) => ({
            ...value,
            createdAt: createdAt ? new Date(createdAt) : undefined,
            updatedAt: updatedAt ? new Date(updatedAt) : undefined,
            ...(deletedAt ? { deletedAt: new Date(deletedAt) } : undefined),
          })),
      } as CustomSelectField | CustomMultiSelectField;
    default: {
      const unknownField = field as CustomFieldDTO;
      // this shouldn't happen because we've covered all data types, but if it does...
      // log a warning and drop this Field
      console.warn(
        `CustomField (${unknownField.name}) of type '${unknownField.dataType}' failed to parse`
      );
      return undefined;
    }
  }
};

export const parseCustomFieldList = (
  customFields: CustomFieldDTO[]
): CustomField[] =>
  customFields.map(parseCustomField).filter((f): f is CustomField => f != null);

export function serializeCustomFieldValue(
  dataType: "numeric",
  value: CustomNumericField["defaultValue"]
): CustomNumericFieldDTO["defaultValue"] | undefined;
export function serializeCustomFieldValue(
  dataType: "date",
  value: CustomDateField["defaultValue"]
): CustomDateFieldDTO["defaultValue"] | undefined;
export function serializeCustomFieldValue(
  dataType: CustomField["dataType"],
  value: CustomField["defaultValue"]
): CustomFieldDTO["defaultValue"] | undefined;
export function serializeCustomFieldValue(
  dataType: CustomField["dataType"],
  value: CustomField["defaultValue"]
): CustomFieldDTO["defaultValue"] | undefined {
  switch (dataType) {
    case "numeric": {
      return value === null
        ? null
        : typeof value === "string"
          ? value
          : Number.isFinite(value) && value !== undefined
            ? value.toString()
            : undefined;
    }
    case "date": {
      return value === null
        ? null
        : value instanceof Date
          ? formatDateForBackend(value)
          : undefined;
    }
    default:
      // FIXME Remove assertion
      return value as CustomFieldDTO["defaultValue"] | undefined;
  }
}

export const serializeCustomField = (
  field: SelectivePartial<CustomField, "dataType">
): SelectivePartial<CustomFieldDTO, "dataType"> => {
  const serializedDates = {
    createdAt: field.createdAt ? field.createdAt.toISOString() : undefined,
    deletedAt: field.deletedAt ? field.deletedAt.toISOString() : undefined,
    updatedAt: field.updatedAt ? field.updatedAt.toISOString() : undefined,
  };

  switch (field.dataType) {
    case "numeric":
      return {
        ...field,
        ...serializedDates,
        defaultValue: serializeCustomFieldValue(
          "numeric",
          field.defaultValue
        ) as CustomNumericFieldDTO["defaultValue"],
      };
    case "date":
      return {
        ...field,
        ...serializedDates,
        defaultValue: serializeCustomFieldValue(
          "date",
          field.defaultValue
        ) as CustomDateFieldDTO["defaultValue"],
      };
    case "text":
      return {
        ...field,
        ...serializedDates,
      };
    case "boolean":
      return {
        ...field,
        ...serializedDates,
      };
    case "select":
    case "multi_select":
      return {
        ...field,
        ...serializedDates,
        values:
          field.values &&
          field.values.map(
            ({
              createdAt,
              deletedAt,
              updatedAt,
              ...value
            }): CustomSelectFieldValueDTO => ({
              ...value,
              ...(createdAt ? { createdAt: createdAt.toISOString() } : {}),
              ...(deletedAt ? { deletedAt: deletedAt.toISOString() } : {}),
              ...(updatedAt ? { updatedAt: updatedAt.toISOString() } : {}),
            })
          ),
      };
    default: {
      const unknownField = field as unknown as CustomFieldDTO;

      // this shouldn't happen because we've covered all data types, but if it does...
      // log the error and send the object as is; BE can blow up if it wants/needs
      console.error(
        `CustomField (${unknownField.name}) of type '${unknownField.dataType}' failed to serialize`
      );

      return {
        ...unknownField,
        ...serializedDates,
      };
    }
  }
};

export const getCustomSelectFieldValue = (
  values: CustomSelectFieldValue[],
  lookupValue: CustomFieldDataValue,
  key: Extract<keyof CustomSelectFieldValue, "id" | "displayName"> = "id"
) => values.find(({ [key]: value }) => value === lookupValue);

export const isStandardFieldName = (name: string): boolean =>
  standardFieldMapKeys.includes(name);

export const getCustomFieldDefaultValue = (customField: CustomField) => {
  if (
    customField.defaultValue === null ||
    customField.defaultValue === undefined
  ) {
    return customField.defaultValue;
  }

  switch (customField.dataType) {
    case "select": {
      const value = getCustomSelectFieldValue(
        customField.values,
        customField.defaultValue,
        "displayName"
      );

      return value && value.id;
    }
    case "multi_select": {
      const valueIds = customField.defaultValue.reduce<
        CustomSelectFieldValue["id"][]
      >((valueIds, valueDisplayName) => {
        const value = getCustomSelectFieldValue(
          customField.values,
          valueDisplayName,
          "displayName"
        );

        if (value) {
          valueIds.push(value.id);
        }

        return valueIds;
      }, []);
      return valueIds.length ? valueIds : undefined;
    }
    default:
      return customField.defaultValue;
  }
};
